import type { BaseDocument, Ref } from "./data";
import type { Event } from "./event";
import type { Installation } from "./installation";
import type { GeoJSONPoint } from "./map";

/**
 * - `Manual`: an operator clicked Track subject on the event.
 * - `Auto`: started by auto-tracking on a high or critical event.
 */
export enum SubjectTrackOrigin {
	Auto = "auto",
	Manual = "manual",
}

/**
 * - `Indexing`: the window's clips are being indexed and the seed isn't
 *   chosen yet.
 * - `AwaitingSeed`: the seed clips are indexed and show more than one person;
 *   an operator picks who to follow. The rest of the window keeps indexing.
 * - `Expanding`: the seed is set and hops are being found, while clips are
 *   still indexing or hops are waiting on the verifier.
 * - `Complete`: every clip in the window is indexed and every hop is settled
 *   or left in review. An operator's confirm or reject reopens it.
 * - `NoSubject`: nobody in the seed clips to follow.
 * - `Dismissed`: an operator closed the track.
 * - `Failed`: indexing or expansion gave up.
 */
export enum SubjectTrackStatus {
	AwaitingSeed = "awaiting_seed",
	Complete = "complete",
	Dismissed = "dismissed",
	Expanding = "expanding",
	Failed = "failed",
	Indexing = "indexing",
	NoSubject = "no_subject",
}

/** Which way in time a hop was found from the track's seed. */
export enum TrackDirection {
	Backward = "backward",
	Forward = "forward",
}

/** Who settled a hop that went to review. */
export enum TrackHopDecider {
	Operator = "operator",
	Verifier = "verifier",
}

/**
 * - `Auto`: scored above the accept threshold with a clear margin; joins the
 *   chain and is expanded from without review.
 * - `NeedsReview`: plausible but not certain; waits for an operator.
 * - `Confirmed`: an operator (or the eval's simulated review) said yes. Its
 *   embedding joins the subject gallery and expansion continues from it.
 * - `Rejected`: an operator said no. Never expanded from or re-proposed.
 */
export enum TrackHopState {
	Auto = "auto",
	Confirmed = "confirmed",
	NeedsReview = "needs_review",
	Rejected = "rejected",
}

/**
 * Whether a tracklet's crops carry color. Measured from the pixels by the
 * ai-track indexer, so a color camera in night IR mode reads `Grayscale`
 * just like a thermal camera. Appearance is never compared across the two:
 * the ReID model is trained on color and can't match one person across them.
 */
export enum TrackletModality {
	Color = "color",
	Grayscale = "grayscale",
}

export enum TrackObjectClass {
	Person = "person",
	Vehicle = "vehicle",
}

/** A walkable connection from one sensor to another, with the expected walk time. */
export interface SensorLink {
	/** Negative when the two views overlap, so the subject can be on both at once. */
	maxTransitSeconds: number;
	minTransitSeconds: number;
	sensor: string;
}

/** A tracklet as stored: one per person per clip, shared by every track that indexes the clip. */
export interface StoredTracklet extends BaseDocument, Tracklet {}

/**
 * A subject followed across an installation's cameras from one event.
 *
 * The whole window (`windowStart` to `windowEnd` on every sensor) is indexed
 * up front, and the hops are recomputed from the indexed tracklets each time
 * more clips finish, so `hops` is derived state. What it's derived from is
 * kept: the seeds, the operator's `reviews` and the verifier's `verdicts`, so
 * a rerun never asks Gemini the same question twice or loses a decision.
 */
export interface SubjectTrack extends BaseDocument {
	/** Every clip in the window: the indexing plan. */
	clips: string[];
	/** Kinde user id of the operator who started it; absent for auto tracks. */
	createdBy?: string;
	event: Ref<Event>;
	/** The `revision` the stored `hops` were computed from. */
	expandedRevision?: number;
	/** Lease held by the expansion run that's recomputing the track; at most one at a time. */
	expandingUntil?: Date;
	/**
	 * While an expansion run asks Gemini about candidates: which ones it's
	 * still waiting on. The hops found so far are saved alongside, so a page
	 * shows them as they come; cleared when the run saves its final hops.
	 */
	expansionProgress?: TrackExpansionProgress;
	/** Why the track failed, when `status` is `Failed`. */
	failureReason?: string;
	hops: TrackHop[];
	horizonMinutes: number;
	/** Clips from `clips` whose tracklets have been loaded. */
	indexedClips: string[];
	installation: Ref<Installation>;
	origin: SubjectTrackOrigin;
	/** Operator decisions on hops, newest last; a later one on the same tracklet wins. */
	reviews: TrackReview[];
	/**
	 * Bumped by everything that changes what the hops are derived from (a clip
	 * loaded, a seed set, an operator decision), so an expansion run knows
	 * when it's caught up.
	 */
	revision: number;
	/** The event's own clips, where the seed is chosen. */
	seedClips: string[];
	/** From the Gemini subject pick, for auto tracks. */
	seedPick?: TrackSeedPick;
	/** The seed sheets the ai-track worker drew for an auto track's seed clips. */
	seedSheets?: { key: string; mediaKey: string }[];
	/** The tracklet(s) followed: one for a manual track, the subject pick's union for an auto one. */
	seedTracklets: string[];
	/** Per-track overrides over the default thresholds. */
	settings?: Partial<TrackSettings>;
	status: SubjectTrackStatus;
	/** Set on an auto track whose subject pick was low confidence. */
	subjectUnconfirmed?: boolean;
	/** Verifier answers by candidate tracklet, so a rerun reuses them. */
	verdicts: TrackVerdictRecord[];
	windowEnd: Date;
	windowStart: Date;
}

/**
 * A track as Shadow Realm shows it: the stored track, a sighting for the seed
 * and every hop, and while it waits on its seed, the people to choose from.
 */
export interface SubjectTrackView extends SubjectTrack {
	seedCandidates?: TrackSighting[];
	/** Every camera at the installation, for the map. */
	sensors: { geolocation?: GeoJSONPoint; id: string; name: string }[];
	/** The seed's and every hop's sighting, by tracklet id. */
	sightings: Record<string, TrackSighting>;
	/**
	 * False when no camera at the installation has links, so walking times
	 * come from the distance between cameras (the geolocation fallback).
	 */
	topologyConfigured: boolean;
}

/** An expansion run's progress through the verifier (`SubjectTrack.expansionProgress`). */
export interface TrackExpansionProgress {
	/** Candidate tracklets Gemini hasn't answered yet. */
	checking: string[];
	/** Review round, from 0: each round's confirmations can open the next. */
	round: number;
	updatedAt: Date;
}

export interface TrackHop {
	/** Set on a hop settled from review: by an operator, or by the Gemini verifier. */
	decidedBy?: TrackHopDecider;
	direction: TrackDirection;
	endTimestamp: Date;
	mediaKey: string;
	/** The tracklet this hop was found from: the seed or another hop. */
	parentTracklet: string;
	score: TrackHopScore;
	sensor: string;
	startTimestamp: Date;
	state: TrackHopState;
	tracklet: string;
}

export interface TrackHopScore {
	/** Mean of the top cosine similarities against same-modality gallery embeddings; 0 for a cross-modality hop. */
	appearance: number;
	/** appearance × topology. */
	combined: number;
	/**
	 * Set when the candidate has no same-modality gallery to compare with
	 * (e.g. a thermal sighting of a subject seeded on color). It joined on
	 * time overlap with an accepted hop, not on appearance.
	 */
	crossModality?: boolean;
	/** combined minus the best rival on the same sensor at the same time. */
	margin: number;
	/** 1 inside the transit window, decaying outside it. */
	topology: number;
	/** The Gemini verifier's answer, when the hop went to review and was asked. */
	verifier?: { answer: TrackVerdictAnswer; model: string; reason: string };
}

/** One person (or vehicle) seen continuously in one clip, as written by the ai-track indexer. */
export interface Tracklet {
	bboxSamples: TrackletBboxSample[];
	bestCropKey: string;
	cropKeys: string[];
	detectorVersion: string;
	/** L2-normalized ReID embedding (512 for OSNet); compare by dot product. */
	embedding: number[];
	endTimestamp: Date;
	id: string;
	installation: string;
	mediaKey: string;
	modality: TrackletModality;
	objectClass: TrackObjectClass;
	reidModelVersion: string;
	sensor: string;
	startTimestamp: Date;
}

/** A sparse bounding-box sample: `t` is seconds from clip start, `bbox` is x1, y1, x2, y2 normalized to 0–1. */
export interface TrackletBboxSample {
	bbox: [number, number, number, number];
	t: number;
}

/** One operator decision on a hop. */
export interface TrackReview {
	at: Date;
	/** Kinde user id. */
	by: string;
	confirmed: boolean;
	tracklet: string;
}

/** Why the Gemini subject pick chose the seeds of an auto track. */
export interface TrackSeedPick {
	confidence: "high" | "low";
	reason: string;
}

/**
 * A seed sheet as the ai-track worker writes it: frames of one clip with
 * every person boxed and numbered, a number staying with its tracklet in
 * every frame. Frame keys are JPEGs in the uploads bucket.
 */
export interface TrackSeedSheet {
	frames: { key: string; numbers: number[]; t: number }[];
	/** Number by tracklet id. */
	numbers: Record<string, number>;
}

/** Thresholds and limits for expansion; per-track overrides layer over the defaults. */
export interface TrackSettings {
	acceptThreshold: number;
	/** Seconds either side of the seed searched. */
	horizonMinutes: number;
	/**
	 * Topology score for a gap longer than the transit window plus slack: the
	 * person went somewhere no camera covers and came back. Such hops can only
	 * reach review, never auto. 0 drops them instead.
	 */
	longGapTopologyFloor: number;
	/**
	 * Largest share of a sighting's boxes that can touch the left or right edge
	 * of the frame for it to be auto-accepted. Above it, the person is mostly
	 * cut off by the edge (a sliver of legs or a sleeve), so it can only reach
	 * review. 1 turns the rule off.
	 */
	maxAutoEdgeShare: number;
	/** Hops that move to a different sensor; same-sensor fragments don't count. */
	maxHops: number;
	/**
	 * Shortest sighting that can be auto-accepted. A shorter one (a person
	 * clipped at the frame edge, a flicker) has too few crops to trust, so it
	 * can only reach review.
	 */
	minAutoSeconds: number;
	minMargin: number;
	reviewThreshold: number;
	/** Same-sensor gap accepted as one continuous sighting (clip boundary or a broken track). */
	sameSensorMaxGapSeconds: number;
	/** Gallery similarities averaged for the appearance score. */
	topK: number;
	/** How fast the topology score falls off outside the transit window. */
	topologyDecaySeconds: number;
	/** How far outside the transit window candidates are still considered. */
	topologySlackSeconds: number;
}

/** One person on one camera (a tracklet), with what a page needs to show and play it. */
export interface TrackSighting {
	bboxSamples: TrackletBboxSample[];
	/** When the clip starts; `bboxSamples[].t` counts from here. */
	clipStart: Date;
	/** Signed playback URL for the clip. */
	clipUrl: string;
	/** Signed crop URLs, best first. */
	cropUrls: string[];
	endTimestamp: Date;
	mediaKey: string;
	modality: TrackletModality;
	sensor: { id: string; name: string };
	startTimestamp: Date;
	tracklet: string;
}

/** What camera topology needs to know about a sensor. */
export interface TrackTopologySensor {
	geolocation?: GeoJSONPoint;
	id: string;
	links?: SensorLink[];
}

/**
 * The verifier's answer on a candidate. "unsure" means the crops can't settle
 * it either way (legs only, blur, thermal with nothing distinctive), which
 * isn't evidence of someone else, so the hop stays in review.
 */
export type TrackVerdictAnswer = "different" | "same" | "unsure";

/** A stored verifier answer on one candidate tracklet. */
export interface TrackVerdictRecord {
	answer: TrackVerdictAnswer;
	at: Date;
	model: string;
	/** `TRACK_VERIFIER_PROMPT_VERSION`; a verdict from another version is asked again. */
	promptVersion: number;
	reason: string;
	tracklet: string;
}

/** Expected seconds between leaving one sensor's view and entering another's. */
export interface TransitWindow {
	maxSeconds: number;
	minSeconds: number;
}
